import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Box, Button, Checkbox, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Icon, InputWithIcon, toast } from '@livable/saas-react-components';
import useStyles from './styles';
import { useStore } from 'src/stores';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { BillActionsMenu } from './BillActionsMenu';
import { ConfirmActionModal } from './BillDetails/ConfirmActionModal';
import { BillsFilterMenu } from './BillsFilterMenu';
import { BillsSummaryCards } from './BillsSummaryCards';
import { BillBatchFilterBanner } from './BillBatchFilterBanner';
import { BillsToolbarFilters } from './BillsToolbarFilters';
import { AttachBillModal } from './AttachBillModal';
import { UploadBillModal } from './UploadBillModal';
import { BillIntakeAliasModal } from 'src/pages/AdminPage/BillIntakeAliasModal';
import { UploadedBillDetailsModal } from './UploadedBillDetailsModal';
import { BILL_STATUSES, getDeleteBlockedReason, getPropertyAddressIndex, mapCompanyBillToRow } from './companyBillRows';
import { EMPTY_BILL_FILTERS, getBillFilterOptions, hasActiveBillFilters, matchesBillFilters } from './billsFilters';
import { getAppfolioPushChipProps, getAppfolioPushMenuHint, getAppfolioPushSortRank } from './appfolioPushRow';
import { useBillsAppfolioPush } from './useBillsAppfolioPush';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;
const POLL_INTERVAL_MS = 5000;

/** Active (currently-trusted) allowed senders in a tile-count response, or undefined if unreadable. */
const toActiveCount = resp => {
  const senders = resp && resp.isSuccessful !== false ? resp.response : null;
  return Array.isArray(senders) ? senders.filter(sender => sender.isActive).length : undefined;
};

/**
 * The coarse cut across the top. Deliberately separate from the status filter in the Filters menu:
 * the tab is where you are, the filter is what you asked for, and folding one into the other makes
 * changing tab silently discard a filter the user set.
 */
const BILL_TABS = [{
  key: 'all',
  label: 'All bills',
  matches: () => true
}, {
  key: 'needsReview',
  label: 'Needs review',
  matches: row => row.status === BILL_STATUSES.needsProperty
}, {
  key: 'processed',
  label: 'Processed',
  matches: row => row.status === BILL_STATUSES.validated
}];
function formatUsd(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}

/**
 * Progress columns count what is still outstanding, not what is finished. Once nothing is left the
 * column collapses to a single success tag ("Allocated", "Pushed") instead of "0/n Pending".
 */
function progressChipProps(progress, doneLabel) {
  var _progress$total, _progress$done;
  const of = (_progress$total = progress === null || progress === void 0 ? void 0 : progress.total) !== null && _progress$total !== void 0 ? _progress$total : 0;
  const pending = Math.max(of - ((_progress$done = progress === null || progress === void 0 ? void 0 : progress.done) !== null && _progress$done !== void 0 ? _progress$done : 0), 0);
  if (of > 0 && pending === 0) return {
    label: doneLabel,
    variant: 'success'
  };
  return {
    label: "".concat(pending, "/").concat(of, " Pending"),
    variant: 'inactive'
  };
}

/** Same HTML for every plain-text table cell: div.MuiTypography (body2, primary). */
function BillCellText(_ref) {
  let children = _ref.children,
    className = _ref.className,
    _ref$noWrap = _ref.noWrap,
    noWrap = _ref$noWrap === void 0 ? false : _ref$noWrap,
    title = _ref.title,
    sx = _ref.sx;
  const nowrapSx = noWrap ? {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    maxWidth: '100%',
    minWidth: 0
  } : {};
  return /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.primary",
    component: "div",
    className: className,
    noWrap: noWrap,
    title: title,
    sx: _objectSpread(_objectSpread({}, nowrapSx), sx)
  }, children);
}

/** Which tone the status pill is drawn in. Keys into the `billsStatus_*` rules. */
function billStatusTone(status) {
  switch (status) {
    case BILL_STATUSES.validated:
      return 'success';
    case BILL_STATUSES.processing:
    case BILL_STATUSES.extracted:
      return 'info';
    case BILL_STATUSES.needsProperty:
    // Rejected shares the warning tone with Needs review because it shares the meaning: this one is
    // waiting on a decision from you. Not 'error' -- nothing failed, we declined it, and the PM can
    // reverse that by approving the sender.
    case BILL_STATUSES.rejected:
      return 'warning';
    // Duplicate is neutral on purpose: it is informational, there is nothing to do, and colouring it
    // would pull the eye to the one row in the list that needs no attention.
    case BILL_STATUSES.duplicate:
      return 'neutral';
    case BILL_STATUSES.failed:
      return 'error';
    default:
      return 'neutral';
  }
}
function getSortComparable(row, orderBy, getPushRank) {
  var _row$fileName, _row$status, _row$allocation$done, _row$allocation, _row$allocation$total, _row$allocation2, _row$utilityIcons$len, _row$utilityIcons, _row$propertiesLines, _row$account, _row$provider, _row$servicePeriod, _row$days, _row$amount, _row$invoiceNumber, _row$dueDate, _row$receivedAt, _row$from;
  switch (orderBy) {
    case 'fileName':
      return (_row$fileName = row.fileName) !== null && _row$fileName !== void 0 ? _row$fileName : '';
    case 'status':
      return (_row$status = row.status) !== null && _row$status !== void 0 ? _row$status : '';
    case 'allocation':
      return ((_row$allocation$done = (_row$allocation = row.allocation) === null || _row$allocation === void 0 ? void 0 : _row$allocation.done) !== null && _row$allocation$done !== void 0 ? _row$allocation$done : 0) / Math.max((_row$allocation$total = (_row$allocation2 = row.allocation) === null || _row$allocation2 === void 0 ? void 0 : _row$allocation2.total) !== null && _row$allocation$total !== void 0 ? _row$allocation$total : 1, 1);
    // Ranked by what the push status says, not by the row's own roll-up: that one counts allocation
    // pushes, which is a different thing from whether this document reached AppFolio.
    case 'appfolioPush':
      return getPushRank(row);
    case 'utilities':
      return (_row$utilityIcons$len = (_row$utilityIcons = row.utilityIcons) === null || _row$utilityIcons === void 0 ? void 0 : _row$utilityIcons.length) !== null && _row$utilityIcons$len !== void 0 ? _row$utilityIcons$len : 0;
    case 'properties':
      return ((_row$propertiesLines = row.propertiesLines) !== null && _row$propertiesLines !== void 0 ? _row$propertiesLines : []).join(' ');
    case 'account':
      return (_row$account = row.account) !== null && _row$account !== void 0 ? _row$account : '';
    case 'provider':
      return (_row$provider = row.provider) !== null && _row$provider !== void 0 ? _row$provider : '';
    case 'servicePeriod':
      return (_row$servicePeriod = row.servicePeriod) !== null && _row$servicePeriod !== void 0 ? _row$servicePeriod : '';
    case 'days':
      return (_row$days = row.days) !== null && _row$days !== void 0 ? _row$days : 0;
    case 'amount':
      return (_row$amount = row.amount) !== null && _row$amount !== void 0 ? _row$amount : 0;
    case 'invoiceNumber':
      return (_row$invoiceNumber = row.invoiceNumber) !== null && _row$invoiceNumber !== void 0 ? _row$invoiceNumber : '';
    case 'dueDate':
      return (_row$dueDate = row.dueDate) !== null && _row$dueDate !== void 0 ? _row$dueDate : '';
    // Sorted on the timestamp, not the words: "2 hours ago" and "10 min ago" do not order.
    case 'received':
      return (_row$receivedAt = row.receivedAt) !== null && _row$receivedAt !== void 0 ? _row$receivedAt : '';
    case 'from':
      return (_row$from = row.from) !== null && _row$from !== void 0 ? _row$from : '';
    default:
      return null;
  }
}
export const BillsTab = observer(() => {
  var _store$companyBills$l, _BILL_TABS$find, _selectedRows$, _selectedRows$2, _selectedRows$3, _selectedRows$4, _selectedRows$5, _store$companyBills$g, _store$companyBills$g2;
  const classes = useStyles();
  const store = useStore();
  const isUserLivableStaff = store.account.userProfile.isUserLivableStaff;
  // localStorage can hold the literal strings 'null'/'undefined' when no company is selected;
  // guard both, exactly as BillIntakeAliasBanner does, so we never call /companies/undefined/...
  const intakeCompanyId = localStorage.getItem(USER_SETTINGS_KEYS.company);
  const hasIntakeCompany = !!intakeCompanyId && intakeCompanyId !== 'null' && intakeCompanyId !== 'undefined';
  const isAppfolio = store.company.companyFeatureStore.isAppfolio;
  /**
   * The flag gates the trigger only — push status is readable either way, and a bill pushed before the
   * flag was turned off still says so. So the column reads for every AppFolio company; only the action
   * behind it waits on the flag.
   */
  const isApPushEnabled = isAppfolio && store.account.featureFlagsStore.getIsPushUtilityBillAppFolioEnabled;
  const addressById = getPropertyAddressIndex(store.company.propertiesList);
  const rows = store.companyBills.items.map(bill => mapCompanyBillToRow(bill, addressById));
  const uploadsInFlight = store.bulkUpload.inFlightCount;
  const _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  /**
   * Deep link (LB-5828): the batch-summary CTA lands on /bills?batchId={id}&tab={hint}. Read here,
   * above the list fetch, so the fetch itself is scoped to the event server-side — the whole batch
   * comes back regardless of how recent it is, instead of a client-side filter over just the first
   * page of the company-wide list. `null` (no batchId) fetches the whole company as before.
   */
  const deepLinkBatchId = searchParams.get('batchId');
  const deepLinkTab = searchParams.get('tab');
  const appfolioPush = useBillsAppfolioPush({
    isEnabled: isAppfolio,
    // A queued push shows up as the row turning pending, which is what arms the refresh below.
    onPushed: () => store.companyBills.getCompanyBillsAsync.run({
      batchId: deepLinkBatchId || undefined
    }, [])
  });
  const canTriggerPush = isApPushEnabled && appfolioPush.isVisible;

  // Re-fetches when the batch scope changes too: clicking the CTA (or "Show all bills") swaps the
  // batchId in the URL, and the list must reload to the new scope.
  useEffect(() => {
    store.companyBills.getCompanyBillsAsync.run({
      batchId: deepLinkBatchId || undefined
    }, []);
  }, [store.companyBills, deepLinkBatchId]);
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    orderBy = _useState2[0],
    setOrderBy = _useState2[1];
  const _useState3 = useState('asc'),
    _useState4 = _slicedToArray(_useState3, 2),
    order = _useState4[0],
    setOrder = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    page = _useState6[0],
    setPage = _useState6[1];
  const _useState7 = useState(DEFAULT_ROWS_PER_PAGE),
    _useState8 = _slicedToArray(_useState7, 2),
    rowsPerPage = _useState8[0],
    setRowsPerPage = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    selected = _useState0[0],
    setSelected = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    searchQuery = _useState10[0],
    setSearchQuery = _useState10[1];
  const _useState11 = useState(EMPTY_BILL_FILTERS),
    _useState12 = _slicedToArray(_useState11, 2),
    filters = _useState12[0],
    setFilters = _useState12[1];
  const _useState13 = useState(BILL_TABS[0].key),
    _useState14 = _slicedToArray(_useState13, 2),
    activeTab = _useState14[0],
    setActiveTab = _useState14[1];
  const _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    isUploadBillModalOpen = _useState16[0],
    setIsUploadBillModalOpen = _useState16[1];
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    selectedBillId = _useState18[0],
    setSelectedBillId = _useState18[1];
  const _useState19 = useState(null),
    _useState20 = _slicedToArray(_useState19, 2),
    attachBillId = _useState20[0],
    setAttachBillId = _useState20[1];
  const _useState21 = useState(null),
    _useState22 = _slicedToArray(_useState21, 2),
    rowToDelete = _useState22[0],
    setRowToDelete = _useState22[1];
  const _useState23 = useState(false),
    _useState24 = _slicedToArray(_useState23, 2),
    isConfirmingBulkDelete = _useState24[0],
    setIsConfirmingBulkDelete = _useState24[1];
  // LB-5810: whether the staff-only bill-intake-address modal is open (false = closed).
  const _useState25 = useState(false),
    _useState26 = _slicedToArray(_useState25, 2),
    isIntakeSettingsOpen = _useState26[0],
    setIsIntakeSettingsOpen = _useState26[1];
  // LB-5812: count of currently-trusted (active) allowed senders, for the summary tile. undefined
  // while loading or when there is no company context. The list is not cached in the store (it is
  // read inside the Allowed senders modal), so fetch it once here for the figure; it refreshes when
  // the page reloads rather than reacting live to changes made in the modal.
  const _useState27 = useState(undefined),
    _useState28 = _slicedToArray(_useState27, 2),
    allowedSendersCount = _useState28[0],
    setAllowedSendersCount = _useState28[1];
  useEffect(() => {
    if (!hasIntakeCompany) {
      setAllowedSendersCount(undefined);
      return undefined;
    }
    let active = true;
    // Clear first so a company switch shows "—" while re-fetching rather than the previous
    // company's count until the new one arrives.
    setAllowedSendersCount(undefined);
    (async () => {
      const resp = await store.allowedSenders.getAllowedSendersForTileAsync.run();
      if (!active) return;
      setAllowedSendersCount(toActiveCount(resp));
    })();
    return () => {
      active = false;
    };
  }, [store.allowedSenders, hasIntakeCompany, intakeCompanyId]);
  const selectedBill = selectedBillId ? store.uploadedBills.bills.find(bill => bill.billId === selectedBillId) : null;

  /**
   * Extraction keeps moving bills along in the background, and so does an AP push once it is queued —
   * AppFolio finishes that one through Kafka, seconds after the trigger answered. So the list is
   * refreshed while anything is still on its way, whichever pipeline is carrying it.
   *
   * It rests while the details view is open: that view reads its own bill, and the whole list is worth
   * reading again once, on the way out.
   */
  const hasWorkInFlight = (store.companyBills.hasBillsInProgress || store.companyBills.hasUnsettledApPush) && !selectedBillId;

  /**
   * Refreshes are chained instead of scheduled on a fixed interval: `run` cancels the request that
   * is still in flight, so a tick landing mid-pass would abort it. The gap is measured from the end
   * of one pass to the next.
   */
  useEffect(() => {
    if (!hasWorkInFlight) return undefined;
    let isActive = true;
    let timeoutId = null;
    const poll = async () => {
      await store.companyBills.getCompanyBillsAsync.run({
        batchId: deepLinkBatchId || undefined
      }, []);
      if (isActive) timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWorkInFlight, store.companyBills, deepLinkBatchId]);

  /**
   * The one bill being looked at, read as soon as it is opened — the list carries no extraction
   * payload, and waiting for the next pass over the whole list to fetch it made opening the view
   * feel slow. It is read again while the pipeline still has the bill, and left alone once it lands.
   */
  useEffect(() => {
    if (!selectedBillId) return undefined;
    let isActive = true;
    let timeoutId = null;
    const readBill = async () => {
      await store.uploadedBills.pollUploadedBillAsync.run({
        billId: selectedBillId
      });
      if (isActive && store.uploadedBills.isWorthReadingAgain(selectedBillId)) {
        timeoutId = setTimeout(readBill, POLL_INTERVAL_MS);
      }
    };
    readBill();
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedBillId, store.uploadedBills]);

  /**
   * Deep link: notification emails point at /bills?billId={id}. The bill is registered in the
   * session store the same way "View details" does, and the details effect above then reads it by
   * id — so the link works even before the list has loaded the row. Closing the details view clears
   * the param, so a refresh shows the list rather than reopening the bill.
   */
  const deepLinkBillId = searchParams.get('billId');
  useEffect(() => {
    if (!deepLinkBillId) return;
    if (!store.uploadedBills.bills.some(bill => bill.billId === deepLinkBillId)) {
      store.uploadedBills.addUploadedBill({
        billId: deepLinkBillId
      });
    }
    setSelectedBillId(deepLinkBillId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkBillId]);

  /**
   * The `tab` hint (LB-5828) opens the right top tab when it names one we have. `batchId`/`deepLinkTab`
   * themselves are derived above (they gate the scoped fetch); this only applies the tab selection.
   */
  useEffect(() => {
    if (deepLinkTab && BILL_TABS.some(tab => tab.key === deepLinkTab)) {
      setActiveTab(deepLinkTab);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTab]);

  // Any change of batch scope starts the table back on page 1: the previous scope's page number is
  // meaningless against a different (usually much smaller) set of rows and would otherwise land on an
  // empty page.
  useEffect(() => {
    setPage(0);
  }, [deepLinkBatchId]);

  /**
   * "Show all bills": drop the batch scope AND the tab hint, and return the tab to "All". Clearing
   * only batchId would leave the tab pinned to the hinted subset, so the button would not actually
   * return to the full list.
   */
  const handleClearBatchFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('batchId');
    nextParams.delete('tab');
    setSearchParams(nextParams, {
      replace: true
    });
    setActiveTab(BILL_TABS[0].key);
    setPage(0);
  };

  /**
   * Open any bill's details by id — how a Duplicate's "view the original" jumps. Registered in the
   * session store like the deep link, so the details poll can fetch it even when this session never
   * uploaded it.
   */
  const handleOpenBillById = billId => {
    if (!store.uploadedBills.bills.some(bill => bill.billId === billId)) {
      store.uploadedBills.addUploadedBill({
        billId
      });
    }
    setSelectedBillId(billId);
  };

  /**
   * The list endpoint carries no extraction payload, so the details view is backed by the session
   * store. A bill that was not uploaded in this session is registered here and read on opening.
   */
  const handleViewDetails = row => {
    if (!store.uploadedBills.bills.some(bill => bill.billId === row.id)) {
      store.uploadedBills.addUploadedBill({
        billId: row.id,
        fileName: row.fileName,
        propertyAddress: (row.propertiesLines || [])[0] || ''
      });
    }
    setSelectedBillId(row.id);
  };

  /** Whatever came of the visit — an allocation, an edit — shows on the row once the list is reread. */
  const handleCloseDetails = () => {
    setSelectedBillId(null);
    if (searchParams.has('billId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('billId');
      setSearchParams(nextParams, {
        replace: true
      });
    }
    store.companyBills.getCompanyBillsAsync.run({
      batchId: deepLinkBatchId || undefined
    }, []);
  };

  // Batch deep-link scope (LB-5828). The fetch above is server-scoped to the batch, so once the load
  // for the current scope has landed, `rows` IS that event's bills. Until then — the window between a
  // batchId change (including clearing it) and its refetch installing, and after a refetch that failed
  // — the loaded rows still belong to the previous scope, so show nothing rather than present one
  // scope's rows as another's. `loadedBatchId` is the scope the store's items actually came from.
  const loadedBatchId = (_store$companyBills$l = store.companyBills.loadedBatchId) !== null && _store$companyBills$l !== void 0 ? _store$companyBills$l : null;
  const scopeSynced = loadedBatchId === (deepLinkBatchId || null);
  const scopedRows = !scopeSynced ? [] : deepLinkBatchId ? rows.filter(row => row.batchId === deepLinkBatchId) : rows;
  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredBySearch = !searchLower ? scopedRows : scopedRows.filter(b => {
    var _b$propertiesLines;
    const blob = [b.fileName, b.status, b.account, b.provider, b.invoiceNumber, b.servicePeriod, b.dueDate, b.dateUploaded, b.billSource, b.from, ...((_b$propertiesLines = b.propertiesLines) !== null && _b$propertiesLines !== void 0 ? _b$propertiesLines : [])].filter(Boolean).join(' ').toLowerCase();
    return blob.includes(searchLower);
  });
  const filterOptions = getBillFilterOptions(scopedRows);
  // Counts come off every loaded row in scope, not the filtered set: a tab has to say how much is
  // behind it whether or not the current filters would show any of it. Under a batch deep link,
  // "in scope" is that one event's bills.
  const tabCounts = Object.fromEntries(BILL_TABS.map(tab => [tab.key, scopedRows.filter(tab.matches).length]));
  const matchesActiveTab = ((_BILL_TABS$find = BILL_TABS.find(tab => tab.key === activeTab)) !== null && _BILL_TABS$find !== void 0 ? _BILL_TABS$find : BILL_TABS[0]).matches;
  const filteredByColumns = filteredBySearch.filter(matchesActiveTab).filter(row => matchesBillFilters(row, filters));
  const sortedRows = orderBy == null ? [...filteredByColumns] : [...filteredByColumns].sort((a, b) => {
    const getPushRank = row => getAppfolioPushSortRank(appfolioPush.getState(row.id, row.extractStatus, row.apPush, row.pushInvoiceNumber));
    const aVal = getSortComparable(a, orderBy, getPushRank);
    const bVal = getSortComparable(b, orderBy, getPushRank);
    const cmp = typeof aVal === 'string' ? (aVal || '').localeCompare(bVal || '') : (aVal !== null && aVal !== void 0 ? aVal : 0) - (bVal !== null && bVal !== void 0 ? bVal : 0);
    return order === 'asc' ? cmp : -cmp;
  });
  const start = page * rowsPerPage;
  const paginatedRows = sortedRows.slice(start, start + rowsPerPage);
  const handleSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelected(paginatedRows.map(r => r.id));
    } else {
      setSelected([]);
    }
  };
  const handleSelectOne = id => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = e => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  const handleClearSelection = () => setSelected([]);
  const forgetRow = id => {
    store.uploadedBills.removeUploadedBill(id);
    setSelected(prev => prev.filter(selectedId => selectedId !== id));
    if (selectedBillId === id) setSelectedBillId(null);
  };

  /**
   * Deleting a bill cannot be taken back, so it is confirmed first. The service has the last word —
   * it refuses a bill whose services have been allocated and says which, which is worth reading out.
   */
  const handleDeleteRow = async row => {
    setRowToDelete(null);
    const resp = await store.companyBills.deleteBillAsync.run({
      billId: row.id
    });
    if (!resp) return;
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not deleted',
        body: [resp.message || 'This bill has been allocated and can no longer be deleted.']
      });
      return;
    }
    forgetRow(row.id);
    toast('success', {
      body: ["".concat(row.fileName, " deleted.")]
    });
  };

  /**
   * Deletes what can be deleted out of the selection and keeps the rest.
   *
   * A bill whose services have been allocated is not the dashboard's to remove — those charges live
   * in the allocation service. Rather than refuse the whole batch for one such bill, the ones that
   * can go are deleted and the ones that cannot are named, so the user learns which and why.
   *
   * Deliberately sequential: `run` cancels the request still in flight on the same async action, so
   * deleting in parallel would abort all but the last.
   */
  const handleDeleteSelected = async () => {
    setIsConfirmingBulkDelete(false);
    const chosen = selected.map(id => rows.find(row => row.id === id)).filter(Boolean);
    const deleted = [];
    // Bills held by their allocations never reach the service; the rest can still be refused by it.
    const kept = chosen.filter(row => !row.canDelete);
    for (const row of chosen.filter(item => item.canDelete)) {
      // eslint-disable-next-line no-await-in-loop
      const resp = await store.companyBills.deleteBillAsync.run({
        billId: row.id
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        forgetRow(row.id);
        deleted.push(row);
      } else {
        kept.push(row);
      }
    }
    if (deleted.length) {
      toast('success', {
        body: ["".concat(deleted.length, " bill").concat(deleted.length === 1 ? '' : 's', " deleted.")]
      });
    }
    if (kept.length) {
      const names = kept.slice(0, 3).map(row => row.fileName);
      const rest = kept.length - names.length;
      toast('warning', {
        title: "".concat(kept.length, " bill").concat(kept.length === 1 ? '' : 's', " kept"),
        body: ["Already allocated, so ".concat(kept.length === 1 ? 'it was' : 'they were', " left in place: ") + "".concat(names.join(', ')).concat(rest > 0 ? " and ".concat(rest, " more") : '', ".")]
      });
    }

    // What could not go stays selected, so the reason is still in front of the user.
    setSelected(kept.map(row => row.id));
  };
  const hasSelection = selected.length > 0;
  // Read off the loaded rows rather than the page, so a selection made before paging still resolves.
  const selectedRows = selected.map(id => rows.find(row => row.id === id)).filter(Boolean);
  const isSingleSelection = selected.length === 1;
  // Attach is the remediation for exactly two states: a rejected sender, or a property nobody
  // could resolve. Every other status either has a property already or is still moving on its own.
  const canAttachSelected = isSingleSelection && [BILL_STATUSES.rejected, BILL_STATUSES.needsProperty].includes((_selectedRows$ = selectedRows[0]) === null || _selectedRows$ === void 0 ? void 0 : _selectedRows$.status);
  const blockedFromDelete = selectedRows.filter(row => !row.canDelete).length;
  const deletableCount = selectedRows.length - blockedFromDelete;
  /** The same reading the row menu takes, so one bill cannot be pushable in one place and not the other. */
  const selectedPushState = isSingleSelection ? appfolioPush.getState((_selectedRows$2 = selectedRows[0]) === null || _selectedRows$2 === void 0 ? void 0 : _selectedRows$2.id, (_selectedRows$3 = selectedRows[0]) === null || _selectedRows$3 === void 0 ? void 0 : _selectedRows$3.extractStatus, (_selectedRows$4 = selectedRows[0]) === null || _selectedRows$4 === void 0 ? void 0 : _selectedRows$4.apPush, (_selectedRows$5 = selectedRows[0]) === null || _selectedRows$5 === void 0 ? void 0 : _selectedRows$5.pushInvoiceNumber) : null;
  const selectedPushHint = selectedPushState ? getAppfolioPushMenuHint(selectedPushState) : null;
  /**
   * What is holding the selection back, read over the rows that are actually held. A mixed selection
   * names both reasons rather than whichever happened to come first — being told a bill was allocated
   * when a payable is what holds it sends someone looking in the wrong place.
   */
  const heldRows = selectedRows.filter(row => !row.canDelete);
  const selectionDeleteHint = getDeleteBlockedReason({
    isAllocated: heldRows.some(row => row.isAllocated),
    isPushed: heldRows.some(row => row.isPushed)
  });
  /** Some held and some not: the delete is still worth offering, and worth saying it will not take all. */
  const partialDeleteHint = deletableCount > 0 && heldRows.length > 0 ? "".concat(deletableCount, " of ").concat(selectedRows.length, " will be deleted. ").concat(selectionDeleteHint) : null;
  const hasActiveFilters = Boolean(searchLower) || hasActiveBillFilters(filters) || Boolean(deepLinkBatchId);
  /** An empty table on first load reads as "no bills", so it waits for the first pass to come back. */
  const isLoadingBills = store.companyBills.getCompanyBillsAsync.inProgress;

  // Guards the async refresh below from setting state after the component unmounts (the initial
  // tile-count effect uses its own `active` flag for the same reason).
  const isMountedRef = useRef(true);
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  /**
   * In-page refresh (LB-5820): re-fetch the list and the allowed-senders tile count without a full
   * browser reload. `run` is single-flight, and filters/paging/selection are local state the fetch
   * does not touch, so they survive the refresh.
   */
  const handleRefresh = () => {
    store.companyBills.getCompanyBillsAsync.run({
      batchId: deepLinkBatchId || undefined
    }, []);
    if (hasIntakeCompany) {
      store.allowedSenders.getAllowedSendersForTileAsync.run().then(resp => {
        if (isMountedRef.current) setAllowedSendersCount(toActiveCount(resp));
      });
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.billsRoot
  }, /*#__PURE__*/React.createElement(Tabs, {
    className: classes.billsTabs,
    value: activeTab,
    onChange: (event, next) => {
      setActiveTab(next);
      setPage(0);
    }
  }, BILL_TABS.map(tab => /*#__PURE__*/React.createElement(Tab, {
    key: tab.key,
    value: tab.key,
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.billsTabLabel
    }, tab.label, /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.billsTabCount, " ").concat(activeTab === tab.key ? classes.billsTabCountActive : '')
    }, tabCounts[tab.key]))
  }))), /*#__PURE__*/React.createElement(BillsSummaryCards, {
    needsReviewCount: tabCounts.needsReview,
    allowedSendersCount: allowedSendersCount
  }), deepLinkBatchId && /*#__PURE__*/React.createElement(BillBatchFilterBanner, {
    count: scopedRows.length,
    onClear: handleClearBatchFilter
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbar
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbarLeft
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    size: "small",
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    placeholder: "Search bills...",
    fullWidth: true,
    value: searchQuery,
    onChange: handleSearchChange
  })), /*#__PURE__*/React.createElement(BillsToolbarFilters, {
    filters: filters,
    onChange: next => {
      setFilters(next);
      setPage(0);
    },
    options: filterOptions
  }), /*#__PURE__*/React.createElement(BillsFilterMenu, {
    filters: filters,
    onChange: next => {
      setFilters(next);
      setPage(0);
    },
    options: filterOptions
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    className: "".concat(classes.billsRefreshButton).concat(isLoadingBills ? " ".concat(classes.billsRefreshButtonSpinning) : ''),
    onClick: handleRefresh,
    disabled: isLoadingBills,
    "aria-label": "Refresh bills",
    title: "Refresh",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "reload"
    }),
    'custom-attribute': 'bills-refresh'
  }, "Refresh")), /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbarRight
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: !isUserLivableStaff || !hasIntakeCompany,
    title: !isUserLivableStaff ? 'Bill automation settings are not available yet' : hasIntakeCompany ? 'Manage the bill-intake email address' : 'Select a company to manage its bill-intake address',
    onClick: isUserLivableStaff && hasIntakeCompany ? () => setIsIntakeSettingsOpen(true) : undefined,
    'custom-attribute': 'bills-automation-settings'
  }, "Bill automation settings"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    onClick: () => setIsUploadBillModalOpen(true),
    'custom-attribute': 'bills-upload-bill'
  }, uploadsInFlight > 0 ? "Uploading ".concat(uploadsInFlight, "\u2026") : 'Upload bill'))), /*#__PURE__*/React.createElement(Paper, {
    variant: "outlined",
    className: classes.billsTablePaper
  }, filteredByColumns.length === 0 && !isLoadingBills ? /*#__PURE__*/React.createElement(Box, {
    className: classes.billsTableEmpty
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, deepLinkBatchId && scopedRows.length === 0 ? 'No bills from this email or upload are in the list — they may have been removed, or fall outside the most recent bills loaded. Clear the filter to see every bill.' : hasActiveFilters ? 'No bills match your search and filters.' : 'No bills uploaded yet.')) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TableContainer, {
    className: classes.billsTableContainer
  }, /*#__PURE__*/React.createElement(Table, {
    stickyHeader: true,
    size: "small",
    className: classes.billsTable,
    "aria-label": "bills table"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    padding: "checkbox",
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsStickyCheckbox)
  }, /*#__PURE__*/React.createElement(Checkbox, {
    indeterminate: selected.length > 0 && selected.length < paginatedRows.length,
    checked: paginatedRows.length > 0 && selected.length === paginatedRows.length,
    onChange: handleSelectAll,
    size: "small"
  })), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'status',
    direction: orderBy === 'status' ? order : 'asc',
    onClick: () => handleSort('status')
  }, "Status")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'received',
    direction: orderBy === 'received' ? order : 'asc',
    onClick: () => handleSort('received')
  }, "Received")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'provider',
    direction: orderBy === 'provider' ? order : 'asc',
    onClick: () => handleSort('provider')
  }, "Vendor")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'properties',
    direction: orderBy === 'properties' ? order : 'asc',
    onClick: () => handleSort('properties')
  }, "Property")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'servicePeriod',
    direction: orderBy === 'servicePeriod' ? order : 'asc',
    onClick: () => handleSort('servicePeriod')
  }, "Period")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'amount',
    direction: orderBy === 'amount' ? order : 'asc',
    onClick: () => handleSort('amount')
  }, "Amount")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'from',
    direction: orderBy === 'from' ? order : 'asc',
    onClick: () => handleSort('from')
  }, "From")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'fileName',
    direction: orderBy === 'fileName' ? order : 'asc',
    onClick: () => handleSort('fileName')
  }, "File name")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'allocation',
    direction: orderBy === 'allocation' ? order : 'asc',
    onClick: () => handleSort('allocation')
  }, "Allocation")), isAppfolio ? /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'appfolioPush',
    direction: orderBy === 'appfolioPush' ? order : 'asc',
    onClick: () => handleSort('appfolioPush')
  }, "Appfolio")) : null, /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, "Utilities"), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'account',
    direction: orderBy === 'account' ? order : 'asc',
    onClick: () => handleSort('account')
  }, "Account #")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'days',
    direction: orderBy === 'days' ? order : 'asc',
    onClick: () => handleSort('days')
  }, "Days")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'invoiceNumber',
    direction: orderBy === 'invoiceNumber' ? order : 'asc',
    onClick: () => handleSort('invoiceNumber')
  }, "Invoice #")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'dueDate',
    direction: orderBy === 'dueDate' ? order : 'asc',
    onClick: () => handleSort('dueDate')
  }, "Due")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsStickyActionsHead),
    align: "center"
  }, "Actions"))), /*#__PURE__*/React.createElement(TableBody, null, paginatedRows.map(row => {
    var _row$propertiesLines2, _row$propertiesLines3, _row$utilityTypes, _row$utilityIcons2;
    const pushState = appfolioPush.getState(row.id, row.extractStatus, row.apPush, row.pushInvoiceNumber);
    const appfolioPushChipProps = isAppfolio ? getAppfolioPushChipProps(pushState) : null;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: row.id,
      hover: true,
      className: "".concat(classes.tableBodyRow, " ").concat(classes.billsClickableRow),
      onClick: () => handleViewDetails(row),
      selected: selected.includes(row.id)
    }, /*#__PURE__*/React.createElement(TableCell, {
      padding: "checkbox",
      className: classes.billsStickyCheckbox,
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: selected.includes(row.id),
      onChange: () => handleSelectOne(row.id),
      size: "small"
    })), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.billsStatusChip, " ").concat(classes["billsStatus_".concat(billStatusTone(row.status))]),
      title: row.statusTooltip || row.status
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.billsStatusDot
    }), row.status)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.dateUploaded
    }, row.received)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.provider
    }, row.provider)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsPropertiesCell, " ").concat(classes.billsCellNoWrap)
    }, ((_row$propertiesLines2 = row.propertiesLines) !== null && _row$propertiesLines2 !== void 0 ? _row$propertiesLines2 : []).length === 0 ?
    /*#__PURE__*/
    // A bill nobody has placed is the one thing on this row worth acting on,
    // so it says so rather than showing an empty cell.
    React.createElement(BillCellText, {
      noWrap: true,
      className: classes.billsUnmatchedProperty
    }, "\u2014 Choose a property \u2014") : ((_row$propertiesLines3 = row.propertiesLines) !== null && _row$propertiesLines3 !== void 0 ? _row$propertiesLines3 : []).map((line, i) => /*#__PURE__*/React.createElement(BillCellText, {
      key: i,
      noWrap: true,
      title: line,
      sx: {
        lineHeight: 1.35
      }
    }, line))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.servicePeriod
    }, row.servicePeriod)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: formatUsd(row.amount)
    }, formatUsd(row.amount))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.from,
      className: row.senderEmail ? classes.billsSenderEmail : undefined
    }, row.from)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsFileNameCell, " ").concat(classes.billsCellNoWrap)
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.billsFileNameRow
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "file-text",
      state: "grey"
    }), /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.fileName
    }, row.fileName))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(Chip, _extends({
      className: classes.billsTableChip,
      size: "small"
    }, progressChipProps(row.allocation, 'Allocated')))), isAppfolio ? /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, appfolioPushChipProps ? /*#__PURE__*/React.createElement(Chip, _extends({
      className: classes.billsTableChip,
      size: "small"
    }, appfolioPushChipProps)) : null) : null, /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsUtilitiesCell, " ").concat(classes.billsCellNoWrap)
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.billsUtilitiesIcons,
      title: ((_row$utilityTypes = row.utilityTypes) !== null && _row$utilityTypes !== void 0 ? _row$utilityTypes : []).join(', ')
    }, ((_row$utilityIcons2 = row.utilityIcons) !== null && _row$utilityIcons2 !== void 0 ? _row$utilityIcons2 : []).map((type, uIdx) => /*#__PURE__*/React.createElement(Icon, {
      key: "".concat(row.id, "-util-").concat(uIdx),
      type: type,
      state: "grey"
    })))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.account
    }, row.account)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: String(row.days)
    }, row.days)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.invoiceNumber
    }, row.invoiceNumber)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.dueDate
    }, row.dueDate)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.actionsCell, " ").concat(classes.billsStickyActions),
      align: "center",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement(BillActionsMenu, {
      canDelete: row.canDelete,
      canPush: pushState.canPush,
      deleteDisabledHint: row.deleteBlockedReason,
      onDelete: () => setRowToDelete(row),
      onPush: () => appfolioPush.handlePush(row.id),
      onViewDetails: () => handleViewDetails(row),
      pushDisabledHint: getAppfolioPushMenuHint(pushState),
      showPush: canTriggerPush
    })));
  })))), /*#__PURE__*/React.createElement(Box, {
    className: classes.billsFooter
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.billsFooterActions
  }, hasSelection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleClearSelection,
    "aria-label": "Clear selection",
    size: "small"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.billsFooterCount
  }, selected.length), " bill", selected.length === 1 ? '' : 's', " selected"), /*#__PURE__*/React.createElement("span", {
    className: classes.billsFooterSeparator
  }, "\xB7"), isSingleSelection &&
  /*#__PURE__*/
  // The reason lives on a wrapper: a disabled MUI button swallows pointer
  // events, so a title on the button itself would never show.
  React.createElement("span", {
    title: canAttachSelected ? 'Attach this bill to a property' : 'Only Rejected or Needs review bills can be attached'
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: !canAttachSelected,
    onClick: () => canAttachSelected && setAttachBillId(selectedRows[0].id),
    'custom-attribute': 'bills-selection-attach'
  }, "Attach to property")), isSingleSelection && /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    onClick: () => selectedRows[0] && handleViewDetails(selectedRows[0]),
    'custom-attribute': 'bills-selection-details'
  }, "Bill details"), isSingleSelection && canTriggerPush &&
  /*#__PURE__*/
  // Disabled rather than hidden, and the reason on a wrapper the pointer can
  // reach — a bill already pushed, or one still on its way, should say so here
  // rather than leave the action looking as though it was never offered.
  React.createElement("span", {
    title: selectedPushHint || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: !(selectedPushState !== null && selectedPushState !== void 0 && selectedPushState.canPush),
    onClick: () => selectedRows[0] && appfolioPush.handlePush(selectedRows[0].id),
    'custom-attribute': 'bills-selection-appfolio-push'
  }, "Push to AppFolio")), /*#__PURE__*/React.createElement("span", {
    title: (deletableCount === 0 ? selectionDeleteHint : partialDeleteHint) || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    color: "error"
    // Nothing to offer when every bill chosen is held: opening a confirmation that
    // would delete none of them is worse than saying so here.
    ,
    disabled: deletableCount === 0 || store.companyBills.deleteBillAsync.inProgress,
    onClick: () => setIsConfirmingBulkDelete(true),
    'custom-attribute': 'bills-selection-delete'
  }, "Delete")))), /*#__PURE__*/React.createElement(TablePagination, {
    component: "div",
    count: sortedRows.length,
    page: page,
    onPageChange: handleChangePage,
    rowsPerPage: rowsPerPage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    className: classes.tablePagination
  })))), /*#__PURE__*/React.createElement(UploadBillModal, {
    isOpen: isUploadBillModalOpen,
    handleClose: () => setIsUploadBillModalOpen(false)
  }), isUserLivableStaff && /*#__PURE__*/React.createElement(BillIntakeAliasModal, {
    company: isIntakeSettingsOpen && hasIntakeCompany ? {
      companyId: intakeCompanyId,
      companyName: store.company.name
    } : null,
    onClose: () => setIsIntakeSettingsOpen(false)
  }), /*#__PURE__*/React.createElement(UploadedBillDetailsModal, {
    appfolioPush: appfolioPush,
    bill: selectedBill,
    canTriggerPush: canTriggerPush,
    handleClose: handleCloseDetails
    // The row's verdict, so the view opens with the state the table already showed.
    ,
    listApPush: selectedBillId ? (_store$companyBills$g = (_store$companyBills$g2 = store.companyBills.getBill(selectedBillId)) === null || _store$companyBills$g2 === void 0 ? void 0 : _store$companyBills$g2.apPush) !== null && _store$companyBills$g !== void 0 ? _store$companyBills$g : null : null,
    onOpenBill: handleOpenBillById,
    onAttach: billId => setAttachBillId(billId)
  }), /*#__PURE__*/React.createElement(AttachBillModal, {
    row: attachBillId ? store.companyBills.getBill(attachBillId) : null,
    isOpen: !!attachBillId,
    handleClose: () => setAttachBillId(null),
    onAttached: () => {
      setAttachBillId(null);
      // The bill re-entered the pipeline (Processing/Extracted), which the refreshed list sees
      // as in-progress — so the list poller re-arms on its own.
      store.companyBills.getCompanyBillsAsync.run({
        batchId: deepLinkBatchId || undefined
      }, []);
    }
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: deletableCount === 1 ? 'Delete bill' : "Delete ".concat(deletableCount, " bills"),
    isOpen: isConfirmingBulkDelete,
    message: blockedFromDelete === 0 ? "".concat(selected.length, " bill").concat(selected.length === 1 ? '' : 's', " will be deleted, along with ") + 'everything read from them. This cannot be undone.' : "".concat(deletableCount, " of ").concat(selected.length, " bills will be deleted, along with everything read ") + "from them. The other ".concat(blockedFromDelete, " ").concat(blockedFromDelete === 1 ? 'has' : 'have', " ") + 'already been allocated and will be kept. This cannot be undone.',
    onClose: () => setIsConfirmingBulkDelete(false),
    onConfirm: handleDeleteSelected,
    title: selected.length === 1 ? 'Delete bill?' : "Delete ".concat(selected.length, " bills?")
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Delete bill",
    isOpen: Boolean(rowToDelete),
    message: "".concat((rowToDelete === null || rowToDelete === void 0 ? void 0 : rowToDelete.fileName) || 'This bill', " will be deleted, along with everything read from it. This cannot be undone."),
    onClose: () => setRowToDelete(null),
    onConfirm: () => handleDeleteRow(rowToDelete),
    title: "Delete bill?"
  }));
});